import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDownRight } from "lucide-react";
import { Monogram } from "@/components/Logo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1631396326838-de37e5f8bcbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.4 + i * 0.12 },
  }),
};

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen bg-cream pt-32 pb-16 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end min-h-[calc(100vh-12rem)]">
        {/* Left: headline */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-12 bg-terracotta" />
            <span className="font-sans uppercase tracking-[0.3em] text-xs text-charcoal/60">
              Where wood meets artistry
            </span>
          </motion.div>

          <h1 className="font-serif text-charcoal tracking-tight leading-[0.85] text-6xl sm:text-7xl md:text-8xl xl:text-[8.5rem]">
            {["Custom", "cabinets,"].map((w, i) => (
              <span key={w} className="hero-line-mask">
                <motion.span custom={i} variants={lineReveal} initial="hidden" animate="visible" className="block">
                  {w}
                </motion.span>
              </span>
            ))}
            <span className="hero-line-mask">
              <motion.span custom={2} variants={lineReveal} initial="hidden" animate="visible" className="block italic text-terracotta">
                built to last.
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-10 max-w-md"
          >
            <p className="font-sans text-charcoal/70 text-base leading-relaxed">
              Ashmark is a small-batch woodworking studio crafting bespoke cabinetry and
              architectural millwork — one grain at a time.
            </p>
            <a
              href="#work"
              data-testid="hero-scroll-cta"
              className="group inline-flex items-center gap-2 mt-8 font-sans uppercase tracking-[0.2em] text-xs text-charcoal border-b border-charcoal pb-1 hover:text-terracotta hover:border-terracotta transition-colors duration-300"
            >
              See recent work
              <ArrowDownRight size={16} className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>

        {/* Right: spotlight clipped image */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
            className="relative h-[52vh] lg:h-[72vh] w-full overflow-hidden"
          >
            <motion.img
              src={HERO_IMG}
              alt="Artisan woodworking"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-charcoal/20" />
          </motion.div>

          {/* Area-served floating badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 1.5 }}
            data-testid="hero-area-badge"
            className="absolute -bottom-6 -left-6 lg:-left-16 bg-charcoal text-cream p-6 max-w-[260px] shadow-2xl"
          >
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-terracotta mt-1 shrink-0" />
              <div>
                <p className="font-sans uppercase tracking-[0.25em] text-[10px] text-sage mb-1">Serving</p>
                <p className="font-serif text-lg leading-snug">
                  SW Washington & all of Oregon
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* faint monogram watermark */}
      <Monogram
        stroke="#1e1d1c"
        className="pointer-events-none absolute -right-16 top-1/4 w-[420px] opacity-[0.04] hidden lg:block"
      />
    </section>
  );
}
